// System Health, Data Freshness & Document Pipeline API Service

import api from './api';

// ---------------------------------------------------------------------------
// System Health types
// ---------------------------------------------------------------------------

export interface ComponentHealth {
  status: string; // "healthy" | "degraded" | "unhealthy" | "unknown"
  latency_ms: number | null;
  details: Record<string, unknown> | null;
  error: string | null;
}

export interface SystemHealthResponse {
  overall_status: string;
  timestamp: string;
  components: Record<string, ComponentHealth>;
  celery_queues: Record<string, number> | null;
}

// ---------------------------------------------------------------------------
// Data Freshness types
// ---------------------------------------------------------------------------

export interface DataSourceFreshness {
  source_name: string;
  last_sync_at: string | null;
  staleness_minutes: number | null;
  staleness_status: string; // "fresh" | "stale" | "critical" | "unknown"
  record_count: number | null;
  last_job_status: string | null;
  last_job_error: string | null;
}

export interface DataFreshnessResponse {
  timestamp: string;
  sources: DataSourceFreshness[];
}

// ---------------------------------------------------------------------------
// Document Pipeline types
// ---------------------------------------------------------------------------

export interface StatusBreakdown {
  status: string;
  count: number;
}

export interface PipelineError {
  id: string;
  filename: string | null;
  status: string;
  error_stage: string | null;
  error_message: string | null;
  updated_at: string | null;
}

export interface DocumentPipelineResponse {
  timestamp: string;
  total_documents: number;
  by_status: StatusBreakdown[];
  by_type: StatusBreakdown[];
  recent_errors: PipelineError[];
  avg_processing_seconds: number | null;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const systemHealthService = {
  getSystemHealth: async (): Promise<SystemHealthResponse> => {
    const response = await api.get<SystemHealthResponse>('/stats/system-health');
    return response.data;
  },

  getDataFreshness: async (): Promise<DataFreshnessResponse> => {
    const response = await api.get<DataFreshnessResponse>('/stats/data-freshness');
    return response.data;
  },

  getDocumentPipeline: async (): Promise<DocumentPipelineResponse> => {
    const response = await api.get<DocumentPipelineResponse>('/stats/document-pipeline');
    return response.data;
  },
};
